import { useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AmountFormatted } from '@root/shared/core/format'
import { useOptionalAtom } from '@root/shared/lib'

import { WalletDestinationAmountInput } from './wallet-destination-amount-input'
import { WalletDestinationPercentageInput } from './wallet-destination-percentage-input'

import type { DistributeFundsModel } from '../../../atoms'
import type { DistributeTargetModel } from '../../../atoms/reatom-distribute-target'

type WalletDestinationAmountProps = {
  target: DistributeTargetModel
  model: DistributeFundsModel
}

export const WalletDestinationAmount: React.FC<
  WalletDestinationAmountProps
> = ({ target, model }) => {
  const { t } = useTranslation()

  const [currencyAsset] = useAtom(model.currencyAsset)
  const [amount] = useAtom(target.resolvedAmount)

  const currencyPriceUsd = useOptionalAtom(currencyAsset?.priceUsd)

  const targetAmountUsd = useMemo(
    () => currencyPriceUsd?.multipliedBy(amount),
    [amount, currencyPriceUsd],
  )

  return (
    <Root>
      <TopLine>
        <Label>{t('common.amount')}</Label>
        <Percentage>
          <WalletDestinationPercentageInput target={target} />
        </Percentage>
      </TopLine>
      <AmountContainer>
        <WalletDestinationAmountInput target={target} model={model} />
      </AmountContainer>
      <AmountUsd>
        <AmountFormatted
          amount={targetAmountUsd ?? BigNumber(0)}
          currency="$"
        />
      </AmountUsd>
    </Root>
  )
}

const Root = styled.div`
  height: 53px;
  border-radius: 6px;
  background: var(--appearanceA-4);
  padding-inline: 8px;
  padding-block: 4px 0;

  position: relative;
`

const TopLine = styled.div`
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  margin-bottom: 2px;
`

const Label = styled.div`
  color: var(--text-11);
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const Percentage = styled.div`
  color: var(--primary-9);
  text-align: right;
  text-overflow: ellipsis;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const AmountContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  gap: 4px;

  color: var(--text-12);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const AmountUsd = styled.div`
  overflow: hidden;
  color: var(--text-6);
  text-overflow: ellipsis;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`
